import { Injectable } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  RequestEditCareerDto,
  RequestGetEmpHistoryDto,
  RequestGetPositionDto,
  ResponseGetEmpHistoryDto,
  ResponseGetEmpStatus,
  ResponseGetSpvDto,
  ResponseGetStandarizedDto
} from '../../../shared/nestjs/gd-only/dto/career.dto';
import { ResponseFindPositionDto } from '../../../shared/nestjs/gd-only/dto/position.dto';
import { ResponseWorklocationDto } from '../../../shared/nestjs/gd-only/dto/worklocation.dto';
import { CareerApi } from '../../../shared/nestjs/gd-only/services/career.api';
import { CostCenterNestApi } from '../../../shared/nestjs/gd-only/services/cost-center.api';
import { JobGradeApi } from '../../../shared/nestjs/gd-only/services/job-grade.api';
import { PersonalNestApi } from '../../../shared/nestjs/gd-only/services/personal.api';
import { PositionNestApi } from '../../../shared/nestjs/gd-only/services/position.api';
import { WorklocationNestApi } from '../../../shared/nestjs/gd-only/services/worklocation.api';
import { NestApiProvider } from '../../../shared/nestjs/nest-api';
import { ResponseData, ResponsePaging } from '../../../shared/nestjs/nest-responses';
import {
  CostCenterServiceApi,
  JobGradeServicesApi,
  PositionApi,
  StandardizedServiceApi,
  WorkLocationApi
} from '../../../shared/sdk';
import { SfCareerApi } from './../../../shared/sdk/services/custom/SfCareer';
import { RequestGetEmpDto, ResponseGetEmpDto } from '../../../shared/nestjs/gd-all-in-one/dto/sf-career.dto';
import { AnalyticsProvider } from '../../../providers/analytics/analytics';
import { EventNameAnalytics } from '../../../helpers/event-name-analytics';

@Injectable()
export class CareerBloc {
  private subscriptions: Array<Subscription> = []
  constructor(
    public nestApiProvider: NestApiProvider,
    private careerApi: CareerApi,
    private SfCareerApi: SfCareerApi,
    private personalNestApi: PersonalNestApi,
    private standardizedServiceApi: StandardizedServiceApi,
    private costCenterServiceApi: CostCenterServiceApi,
    private workLocationApi: WorkLocationApi,
    private positionApi: PositionApi,
    private jobGradeServicesApi: JobGradeServicesApi,
    private positionNestApi: PositionNestApi,
    private worklocationNestApi: WorklocationNestApi,
    private jobGradeApi: JobGradeApi,
    private costCenterApi: CostCenterNestApi,
    private analyticsProvider: AnalyticsProvider,
  ) {

  }

  dispose() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  getEmpHistory(payload: RequestGetEmpHistoryDto) {
    return new Promise<Array<ResponseGetEmpHistoryDto>>((resolve, reject) => {
      this.subscriptions.push(
        this.apiGetEmpHistory(payload).subscribe((result) => {
          resolve(result)
        }, error => {
          reject(error);
        })
      )
    })
  }

  getEmp(payload: RequestGetEmpDto) {
    return new Promise<ResponsePaging<ResponseGetEmpDto>>((resolve, reject) => {
      this.subscriptions.push(
        this.careerApi.getEmp(payload)
          .subscribe((result) => {
            resolve(result)
          }, error => {
            reject(error);
          })
      )
    })
  }

  getEmpObserver(payload: RequestGetEmpDto) {
    return this.careerApi.getEmp(payload);
  }

  getPersonal(payload: { empId: any, userId: any }) {
    return new Promise((resolve, reject) => {
      this.apiGetPersonal(payload).subscribe((result) => {
        resolve(result);
      }, error => {
        reject(error);
      })
    })
  }

  getVal(payload: { empId: string }) {
    return new Promise((resolve, reject) => {
      this.subscriptions.push(
        this.apiGetVal(payload)
          .subscribe((result) => {
            resolve(result);
          }, error => {
            reject(error);
          })
      )
    })
  }

  getSpv(payload: { empId: any, companyId: any }) {
    return new Promise<ResponseGetSpvDto>((resolve, reject) => {
      this.subscriptions.push(
        this.apiGetSpv(payload)
          .subscribe((result) => {
            if (result == {} || result == '{}' || result == null) {
              reject(result)
            }
            else {
              resolve(result);
            }
          }, error => {
            reject(error);
          })
      )
    })
  }

  getPhoto(payload: { empId: string, userId: any }) {
    return new Promise<ResponseData<any>>((resolve, reject) => {
      this.subscriptions.push(
        this.apiGetPhoto(payload)
          .subscribe((result) => {
            resolve(result);
          }, error => {
            reject(error);
          })
      )
    })
  }

  getPosition(payload: RequestGetPositionDto) {
    return new Promise<ResponseData<any>>((resolve, reject) => {
      this.subscriptions.push(
        this.apiGetPosition(payload)
          .subscribe((result) => {
            resolve(result);
          }, error => {
            reject(error);
          })
      )
    })
  }

  getSettingStandarized(payload: { companyId: any }) {
    return new Promise<ResponseGetStandarizedDto>((resolve, reject) => {
      this.subscriptions.push(
        this.standardizedServiceApi.getSettingStandardData(payload)
          .subscribe(result => {
            resolve(result);
          }, error => {
            reject(error);
          })
      )
    })
  }

  getEmpStatus(payload: { empId: string }) {
    return new Promise((resolve, reject) => {
      this.subscriptions.push(
        this.SfCareerApi.getEmpStatus(payload)
          .subscribe(
            result => {
              resolve(result);
            }, error => {
              reject(error);
            }
          )
      )
    })
  }

  getEmpStatusNest(): Promise<ResponseData<ResponseGetEmpStatus[]>> {
    return new Promise((resolve, reject) => {
      this.subscriptions.push(
        this.careerApi.getEmpStatus()
          .subscribe(
            result => {
              resolve(result);
            }, error => {
              reject(error);
            }
          )
      )
    })
  }

  getResignReasonNest(): Promise<ResponseData<ResponseGetEmpStatus[]>> {
    return new Promise((resolve, reject) => {
      this.subscriptions.push(
        this.careerApi.getResignReason()
          .subscribe(
            result => {
              resolve(result);
            }, error => {
              reject(error);
            }
          )
      )
    })
  }

  addCareer(payload) {
    return new Promise((resolve, reject) => {
      this.apiAddCareer(payload)
        .subscribe((result) => {
          this.analyticsProvider.trackEvent(EventNameAnalytics.CAREER_SUBMIT_SUCCESS);
          resolve(result);
        }, error => {
          this.analyticsProvider.trackEvent(EventNameAnalytics.CAREER_SUBMIT_FAILED, error);
          reject(error);
        })
    })
  }

  editCareer(payload: RequestEditCareerDto) {
    return new Promise((resolve, reject) => {
      this.subscriptions.push(
        this.apiEditCareer(payload).subscribe((result) => {
          this.analyticsProvider.trackEvent(EventNameAnalytics.CAREER_EDIT_SUBMIT_SUCCESS);
          resolve(result);
        }, error => {
          this.analyticsProvider.trackEvent(EventNameAnalytics.CAREER_EDIT_SUBMIT_FAILED, error);
          reject(error);
        })
      );
    })
  }

  deleteCareer(payload) {
    return new Promise((resolve, reject) => {
      this.careerApi.deleteCareer(payload)
        .subscribe((result) => {
          resolve(result);
        }, error => {
          reject(error);
        })
    })
  }

  updateCareer(payload) {
    return new Promise((resolve, reject) => {
      this.SfCareerApi.updateCareer(payload)
        .subscribe((result) => {
          resolve(result);
        }, error => {
          reject(error);
        })
    })
  }

  findCostCenter(payload: { page: number, limit: number, companyId: any }) {
    return new Promise<Array<any>>(async (resolve, reject) => {
      this.subscriptions.push(
        this.apiFindCostCenter(payload).subscribe((result) => {
          resolve(result);
        }, error => {
          reject(error);
        })
      );
    })
  }

  findPosition(payload: { companyId: any }) {
    return new Promise<Array<ResponseFindPositionDto>>(async (resolve, reject) => {
      this.subscriptions.push(
        this.apiFindPosition(payload).subscribe((result) => {
          resolve(result)
        }, error => {
          reject(error);
        })
      );
    })
  }

  findWorkLocation(payload: { page: number, limit: number, companyId: any }) {
    return new Promise<Array<ResponseWorklocationDto>>(async (resolve, reject) => {
      this.subscriptions.push(
        this.apiFindWorklocation(payload).subscribe((result) => {
          resolve(result);
        }, error => {
          reject(error)
        })
      );
    })
  }

  getJobGrade() {
    return new Promise<Array<any>>(async (resolve, reject) => {
      this.subscriptions.push(
        this.apiGetJobGrade().subscribe((result) => {
          resolve(result);
        }, error => {
          reject(error)
        })
      );
    })
  }

  getResignReason() {
    return new Promise<Array<any>>(async (resolve, reject) => {
      this.subscriptions.push(
        this.SfCareerApi.getResignReason()
          .subscribe(result => {
            resolve(result)
          }, error => {
            reject(error)
          })
      );
    })
  }
  /**
   * Standaritation variable , convert snake_case  to camelCase
   * @param arrData
   */
  convertKeyArrObj(arrData: Array<any>): Promise<Array<any>> {
    return new Promise((resolve) => {
      let tempDataArr = [];

      for (let objData of arrData) {
        objData.relCarttype = null;
        objData.relPersonal = null;
        objData.relJobgrade = null;
        objData.relGrade = null;
        objData.relPosition = null;
        objData.relCostcode = null;
        objData.relWorkloc = null;

        if (objData.hasOwnProperty('rel_cartype')) {
          objData.relCarttype = objData.rel_cartype;
          delete objData.rel_cartype;
        }

        if (objData.hasOwnProperty('rel_costcode')) {
          objData.relCostcode = objData.rel_costcode;
          delete objData.rel_costcode;
        }

        if (objData.hasOwnProperty('rel_person')) {
          objData.relPersonal = objData.rel_person;
          delete objData.rel_person;
        }

        if (objData.hasOwnProperty('rel_grade')) {
          objData.relGrade = objData.rel_grade;
          delete objData.rel_grade;
        }

        if (objData.hasOwnProperty('rel_jobgrade')) {
          objData.relJobgrade = objData.rel_jobgrade;
          delete objData.rel_jobgrade;
        }

        if (objData.hasOwnProperty('rel_pos')) {
          objData.relPosition = objData.rel_pos;
          delete objData.rel_pos;
        }

        if (objData.hasOwnProperty('rel_workloc')) {
          objData.relWorkloc = objData.rel_workloc;
          delete objData.rel_workloc;
        }

        tempDataArr.push(objData);
      }
      resolve(tempDataArr);
    })
  }

  private apiGetPhoto(payload: { empId: string, userId: any }): Observable<any> {
    return Observable.fromPromise(this.nestApiProvider.isGoPathNewExist())
      .mergeMap(isGoPathNewExist => {
        if (isGoPathNewExist) {
          return this.careerApi.getPhoto(payload);
        } else {
          return this.SfCareerApi.getPhoto(payload).pipe(map(response => {
            return {
              data: response[0],
              message: 'successfully'
            }
          }));
        }
      })
  }

  private apiGetVal(payload: { empId: any }): Observable<any> {
    return Observable.fromPromise(this.nestApiProvider.isGoPathNewExist())
      .mergeMap(isGoPathNewExist => {
        if (isGoPathNewExist) {
          return this.careerApi.getVal(payload);
        } else {
          return this.SfCareerApi.getVal(payload).pipe(map(response => {
            return {
              data: response,
              message: 'successfully'
            }
          }));
        }
      })
  }

  private apiGetSpv(payload: { empId: any, companyId: any }): Observable<any> {
    return Observable.fromPromise(this.nestApiProvider.isGoPathNewExist())
      .mergeMap(isGoPathNewExist => {
        if (isGoPathNewExist) {
          return this.careerApi.getSpv(payload);
        } else {
          return this.SfCareerApi.getSpv(payload).pipe(map(response => {
            if (response == 0 || response == 1) {
              return null;
            }
            else {
              return response[0];
            }
          }));
        }
      })
  }

  private apiGetPersonal(payload: { empId: any, userId: any }): Observable<any> {
    return Observable.fromPromise(this.nestApiProvider.isGoPathNewExist())
      .mergeMap(isGoPathNewExist => {
        if (isGoPathNewExist) {
          return this.personalNestApi.getPersonal(payload.empId);
        } else {
          return this.SfCareerApi.getPersonal(payload).pipe(map(response => {
            return {
              data: response[0],
              message: 'successfully'
            }
          }));
        }
      })
  }

  private apiGetPosition(payload: { posCode: any, posNameEn: any }): Observable<any> {
    return Observable.fromPromise(this.nestApiProvider.isGoPathNewExist())
      .mergeMap(isGoPathNewExist => {
        if (isGoPathNewExist) {
          return this.careerApi.getPosition(payload);
        } else {
          return this.SfCareerApi.getPosition(payload).pipe(map(response => {
            return {
              data: response[0],
              message: 'successfully'
            }
          }));
        }
      })
  }

  private apiGetEmpHistory(payload: RequestGetEmpHistoryDto): Observable<any> {
    return Observable.fromPromise(this.nestApiProvider.isGoPathNewExist())
      .mergeMap(isGoPathNewExist => {
        if (isGoPathNewExist) {
          return this.careerApi.getEmpHistory(payload).pipe(map(async (response) => response.data));
        } else {
          return this.SfCareerApi.getEmpHistory(payload).pipe(map(async (response) => {
            return {
              data: await this.convertKeyArrObj(response),
              message: 'successfully'
            }
          }));
        }
      })
  }

  private apiAddCareer(payload: any): Observable<any> {
    return Observable.fromPromise(this.nestApiProvider.isGoPathNewExist())
      .mergeMap(isGoPathNewExist => {
        if (isGoPathNewExist) {
          return this.careerApi.addCareer(payload).pipe(map(response => response.data));
        } else {
          return this.SfCareerApi.addCareer(payload);
        }
      })
  }

  private apiEditCareer(payload: RequestEditCareerDto): Observable<any> {
    return Observable.fromPromise(this.nestApiProvider.isGoPathNewExist())
      .mergeMap(isGoPathNewExist => {
        if (isGoPathNewExist) {
          return this.careerApi.editCareer(payload).pipe(map(response => response.data));
        } else {
          return this.SfCareerApi.editCareer(payload);
        }
      })
  }

  private apiFindPosition(payload: { companyId: any }): Observable<any> {
    return Observable.fromPromise(this.nestApiProvider.isGoPathNewExist())
      .mergeMap(isGoPathNewExist => {
        if (isGoPathNewExist) {
          return this.positionNestApi.findPosition().pipe(map(response => response.data));
        } else {
          return this.positionApi.findPosition(payload);
        }
      })
  }

  private apiFindWorklocation(payload: { page: number, limit: number, companyId: any }): Observable<any> {
    return Observable.fromPromise(this.nestApiProvider.isGoPathNewExist())
      .mergeMap(isGoPathNewExist => {
        if (isGoPathNewExist) {
          return this.worklocationNestApi.findWorkLocation(payload).pipe(map(response => response.data));
        } else {
          return this.workLocationApi.findWorkLocation(payload);
        }
      })
  }

  private apiGetJobGrade(): Observable<any> {
    return Observable.fromPromise(this.nestApiProvider.isGoPathNewExist())
      .mergeMap(isGoPathNewExist => {
        if (isGoPathNewExist) {
          return this.jobGradeApi.getJobGrade().pipe(map(response => response.data));
        } else {
          return this.jobGradeServicesApi.getJobGrade();
        }
      })
  }

  private apiFindCostCenter(payload: { limit: number, page: number }): Observable<any> {
    return Observable.fromPromise(this.nestApiProvider.isGoPathNewExist())
      .mergeMap(isGoPathNewExist => {
        if (isGoPathNewExist) {
          return this.costCenterApi.findCostCenter(payload).pipe(map(response => response.data));
        } else {
          return this.costCenterServiceApi.findCostCenter(payload);
        }
      })
  }

  getMgr(payload: { empId: any, companyId: any }) {
    return new Promise<ResponseGetSpvDto>((resolve, reject) => {
      this.subscriptions.push(
        this.apiGetMgr(payload)
          .subscribe((result) => {
            if (result == {} || result == '{}' || result == null) {
              reject(result)
            }
            else {
              resolve(result);
            }
          }, error => {
            reject(error);
          })
      )
    })
  }

  apiGetMgr(payload: { empId: any, companyId: any }): Observable<any> {
    return Observable.fromPromise(this.nestApiProvider.isGoPathNewExist())
      .mergeMap(isGoPathNewExist => {
        if (isGoPathNewExist) {
          return this.careerApi.getMgr(payload);
        } else {
          return null;
        }
      })
  }
}



// WEBPACK FOOTER //
// ./src/bloc/feature/career/career.bloc.ts